import React, { useEffect, useRef, useState } from 'react'
import { Switch, Spin, notification } from 'antd';
import { Chart } from "react-google-charts";
import { connect, useSelector } from 'react-redux';
import moment from 'moment'
import Segmented from '../components/Segmented'
import { getCompletedStates, getDashboardStatusStates, getFeedbackCategory, getSalesChartData, getScheduledStatesData, submitFeedback } from '../store/dashboard/action';
import TitleWithSelection from '../components/TitleWithSelection';
import SetDefaultPage from '../components/SetDefaultPage';
import { formatHours, formatNumber } from '../helpers/stringFormat';
import { initializePusher, unbindPusher } from '../pusherManager';
import { getCookie } from '../helpers/globals';
import Loader from '../components/Loader';
import { PERMISSIONS } from "../constants/permissions";
import { usePageHeader } from '../context/HeaderContext';

const Dashboard = (props) => {
    const { statusState, completedStates, scheduledStates, feedback, salesChart, feedbackCategory,
        getDashboardStatusStates, getCompletedStates, getScheduledStatesData, getSalesChartData, getFeedbackCategory, onSubmitFeedback } = props
    const fileInputRef = useRef(null);
    const scrollRef = useRef(null);

    const [isComparison, setComparison] = useState(JSON.parse(localStorage.getItem('salesChartComparison')) ?? true)
    const [selecetdScheduleJob, setSelectedScheduleJob] = useState(localStorage.getItem('selectedScheduleJob') ?? 'this_week')
    const [selecetdCompletedJob, setSelectedCompletedJob] = useState(localStorage.getItem('selectedCompletedJob') ?? 'week_to_date')
    const [selecetdSales, setSelectedSales] = useState(localStorage.getItem('selectedSales') ?? 'this_week')
    const [feedback_category, setFeedbackCategory] = useState('')
    const [feedback_msg, setFeedbackMsg] = useState('')
    const [chartData, setChartData] = useState([])
    const [files, setFiles] = useState([])
    const [submitted, setSubmitted] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [hasScrollX, setHasScrollX] = useState(false);

    const orgId = getCookie('active_organization_id')
    const [segmentedOptions] = useState({
        week: [{ label: 'This Week', slug: 'this_week' }, { label: 'Last Week', slug: 'last_week' }],
        month: [{ label: 'This Month', slug: 'this_month' }, { label: 'Last Month', slug: 'last_month' }],
        year: [{ label: 'This Year', slug: moment().year().toString() }, { label: 'Last Year', slug: moment().subtract(1, "year").year().toString() }]
    })
    const [options, setOptions] = useState({
        title: "",
        legend: { position: "bottom" },
        pointSize: 6,
        colors: ["#006EFF", "#97C3FF"],
        lineWidth: 2,
        curveType: "function",
    });

    useEffect(() => {
        getDashboardStatusStates(true)
        getScheduledStatesData(selecetdScheduleJob, true)
        getCompletedStates(selecetdCompletedJob, true)
        getFeedbackCategory()
        getSalesChartData(selecetdSales, true, isComparison)
    }, [])

    useEffect(() => {
        initializePusher("organizations", "choose-event", (data) => {
            if (data.event === "selected" && data?.orgId) {
                getDashboardStatusStates(false)
                getScheduledStatesData(selecetdScheduleJob, false)
                getCompletedStates(selecetdCompletedJob, false)
                getSalesChartData(selecetdSales, false, isComparison)
            }
        })

        initializePusher("planning-board", "move-event", (data) => {
            if (data.event === "planning scheduled" && data?.orgId?.toString() === orgId?.toString()) {
                getDashboardStatusStates(false)
                getScheduledStatesData(selecetdScheduleJob, false)
                getCompletedStates(selecetdCompletedJob, false)
                getSalesChartData(selecetdSales, false, isComparison)
            }
        })

        initializePusher("product-board", "job-event", (data) => {
            if (data.event === "card changed" && data?.orgId?.toString() === orgId?.toString()) {
                getDashboardStatusStates(false)
                getScheduledStatesData(selecetdScheduleJob, false)
                getCompletedStates(selecetdCompletedJob, false)
                getSalesChartData(selecetdSales, false, isComparison)
            }
        });

        return () => {
            unbindPusher("organizations", "choose-event");
            unbindPusher("planning-board", "move-event")
            unbindPusher("product-board", "job-event")
        };
    }, [orgId, selecetdCompletedJob, selecetdSales, selecetdScheduleJob])

    useEffect(() => {
        if (feedback.submitted) {
            setSubmitted(true);
        }
    }, [feedback.submitted])

    useEffect(() => {
        if (submitted) {
            const timeout = setTimeout(() => setSubmitted(false), 120000); // 2 minutes
            return () => clearTimeout(timeout);
        }
    }, [submitted]);

    useEffect(() => {
        const salesChartData = salesChart?.data
        let sortedData = []
        if (Object.keys(salesChartData)?.length > 0) {
            const isWeek = segmentedOptions.week.some(item => item.slug === selecetdSales);
            const isMonth = segmentedOptions.month.some(item => item.slug === selecetdSales);
            const filterDurationLabel = isWeek ? "Week" : (isMonth ? "Month" : "Year");
            const header = isComparison
                ? [
                    "Year",
                    `This ${filterDurationLabel} Sales`,
                    { role: "tooltip", type: "string" },
                    `Last ${filterDurationLabel} Sales`,
                    { role: "tooltip", type: "string" }
                ]
                : ["Year", "Sales", { role: "tooltip", type: "string" },]

            if (isComparison) {
                const keys = Object.keys({
                    ...salesChartData.current,
                    ...salesChartData.previous
                }).sort((a, b) => Number(a) - Number(b))
                
                sortedData = keys.map((key) => [
                    key,
                    Number(salesChartData.current?.[key] ?? 0),
                    `${key} \n This ${filterDurationLabel} Sales $${Number(salesChartData.current?.[key]?.toFixed(2))?.toLocaleString()}`,
                    Number(salesChartData.previous?.[key] ?? 0),
                    `${key} \n Last ${filterDurationLabel} Sales $${Number(salesChartData.previous?.[key]?.toFixed(2))?.toLocaleString()}`
                ])
                setOptions((prev) => ({
                    ...prev,
                    colors: selecetdSales === 'this_week' || selecetdSales === 'this_month' || selecetdSales === moment().year()?.toString() ? ["#006EFF", "#97C3FF"] : ["#97C3FF", "#006EFF"]
                }))
            } else {
                sortedData = Object.entries(salesChart.data)
                if (selecetdSales === "this_month" || selecetdSales === 'last_month') {
                    sortedData = sortedData.sort((a, b) => Number(a[0]) - Number(b[0]))
                }
                sortedData = sortedData.map(([key, value]) => [key, value, `${key} \n Sales: $${Number(value?.toFixed(2))?.toLocaleString()}`]);
            }
    
            setChartData([header, ...sortedData])
        }
    }, [salesChart])

    const { permissions } = useSelector((state) => state.PermissionReducer);
    const canSeePlanningBoard = permissions.includes(PERMISSIONS.DASHBOARD_PLANNING_BOARD_VIEW);
    const canSeeSalesReport = permissions.includes(PERMISSIONS.DASHBOARD_SALES_REPORT_VIEW);

    useEffect(() => {
        if (!canSeePlanningBoard || !canSeeSalesReport) {
            setHasScrollX(false);
            return;
        }

        const checkScroll = () => {
            const el = scrollRef.current;
            if (!el) return;

            setHasScrollX(el.scrollWidth > el.clientWidth);
        };

        // run after mount/render
        checkScroll();

        const el = scrollRef.current;
        if (!el) return;

        const observer = new ResizeObserver(() => {
            checkScroll();
        });

        observer.observe(el);

        return () => {
            observer.disconnect();
        };
        }, [canSeePlanningBoard, canSeeSalesReport]);


    const handleSubmit = () => {
        try {
            if (!feedback_category) {
                notification.open({ type: 'error', message: 'Please select feedback type.' })
                return
            }
            if (!feedback_msg) {
                notification.open({ type: 'error', message: 'Feedback message is required.' })
                return
            }
            const payload = { category: feedback_category, feedback: feedback_msg }
            onSubmitFeedback(payload)
        } catch (error) {
            console.log(error)
        }
    }
    const handleFileChange = (event) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            setFiles(file);
        }
    };

    const handleMouseDown = (e) => {
        setIsDragging(true);
        setStartX(e.pageX - scrollRef.current.offsetLeft);
        setScrollLeft(scrollRef.current.scrollLeft);
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - scrollRef.current.offsetLeft;
        const walk = (x - startX) * 2; // Adjust speed of scroll
        scrollRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleSalesTabChange = (value) => {
        setSelectedSales(value)
        localStorage.setItem('selectedSales', value)
        getSalesChartData(value, true, isComparison)
    }

    usePageHeader(
        <div className='page-header-container'>
            <TitleWithSelection title='Dashboard'>
                <SetDefaultPage routeName='/dashboard' checked={localStorage.getItem('defaultLandingPage') === '/dashboard'} />
            </TitleWithSelection>
        </div>,
        []
    )
    
    return (
        <div className="dashboard">
            <Loader loading={statusState.loading} />
            <Spin spinning={statusState.loading}>
                    <div className='dashboard-container'>
                        {/* status timeline */}
                        {canSeePlanningBoard && (
                            <div className='status-container'>
                                <label className='label-status'>STATUSES</label>
                                <div
                                    ref={scrollRef}
                                    className={`timeline-container ${hasScrollX ? "scroll-x-visible" : ""}`}
                                    onMouseDown={handleMouseDown}
                                    onMouseMove={handleMouseMove}
                                    onMouseLeave={handleMouseUp}
                                    onMouseUp={handleMouseUp}
                                >
                                    <div className='timeline-line' />

                                    {statusState?.data?.map((item, index) => {
                                        return (
                                            <div className='status-item' key={index}>
                                                <label className='status-name'>{item.status_name}</label>
                                                <div className='status'>
                                                    <div className='round'>
                                                        <div className='round-value' style={{
                                                            height: item.total_count === 0 ? 20 : item.total_count === 1 ? 15 : 10 * item.total_count,
                                                            width: item.total_count === 0 ? 20 : item.total_count === 1 ? 15 : 10 * item.total_count,
                                                            opacity: item.total_count === 0 ? 0 : 1
                                                        }}>
                                                            <span>{item.total_count !== 0 ? item.total_count : ''}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className='botton-value'>
                                                    <div className='value'>
                                                        <img alt="" src={require('../assets/images/dollar.svg').default} />
                                                        <span>{formatNumber(item.total_value, 2)}</span>
                                                    </div>
                                                    <div className='value'>
                                                        <img alt="" src={require('../assets/images/clock_icon.svg').default} />
                                                        <span>{item.total_hour}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}

                        {/* status card */}
                        <div className='status-cards'>
                            {/* schedule jobs */}
                            <Spin spinning={scheduledStates.loading && !statusState.loading}>
                                <div className='card'>
                                    <Segmented
                                        options={[{ label: 'This Week', slug: 'this_week' }, { label: 'Next Week', slug: 'next_week' }, { label: 'In 2 Weeks', slug: 'in_two_weeks' }, { label: 'In 3 Weeks', slug: 'in_three_weeks' }]}
                                        selected={selecetdScheduleJob}
                                        onChange={(value) => {
                                            getScheduledStatesData(value, true)
                                            setSelectedScheduleJob(value)
                                            localStorage.setItem('selectedScheduleJob', value)
                                        }}
                                    />
                                    <div className='w-100 gap-2 d-flex flex-column'>
                                        <div className='details'>
                                            <label>Jobs scheduled</label>
                                            <h6>{scheduledStates?.data?.total_scheduled_job ?? 0}</h6>
                                        </div>
                                        <div className='details'>
                                            <label>Hours scheduled (total)</label>
                                            <h6>{formatHours(scheduledStates?.data?.total_hours ?? 0, 'h')}</h6>
                                        </div>
                                    </div>
                                </div>
                            </Spin>

                            {/* completed jobs */}
                            <Spin spinning={completedStates.loading && !statusState.loading}>
                                <div className='card'>
                                    <Segmented
                                        options={[{ label: 'Week to Date', slug: 'week_to_date' }, { label: 'This Month to Date', slug: 'month_to_date' }, { label: 'Year to Date', slug: 'year_to_date' }]}
                                        selected={selecetdCompletedJob}
                                        onChange={(value) => {
                                            setSelectedCompletedJob(value)
                                            getCompletedStates(value, true)
                                            localStorage.setItem('selectedCompletedJob', value)
                                        }}
                                    />
                                    <div className='w-100 gap-2 d-flex flex-column'>
                                        <div className='details'>
                                            <label>Jobs Completed</label>
                                            <h6>{completedStates?.data?.total_completed_job ?? 0}</h6>
                                        </div>
                                        <div className='details'>
                                            <label>Total Value</label>
                                            <h6>{formatNumber(completedStates?.data?.total_amount, 2, '$') ?? 0}</h6>
                                        </div>
                                    </div>
                                </div>
                            </Spin>

                            {submitted ?
                                <div className='card-thank-you'>
                                    <h5>Thank you <br />for your feedback!</h5>
                                </div>
                                :
                                <div className='card-attachment'>
                                    <label className='attach-label'>SEND US YOUR FEEDBACK</label>
                                    <Segmented
                                        options={Object.keys(feedbackCategory?.data)?.length > 0 ? feedbackCategory?.data : []}
                                        selected={feedback_category}
                                        onChange={(value) => setFeedbackCategory(value)}
                                    />
                                    <div className='feedback-input'>
                                        <input
                                            placeholder='Type your message'
                                            onChange={(e) => setFeedbackMsg(e.target.value)}
                                        />
                                        <img alt="" src={require('../assets/images/input-icon.svg').default} />
                                    </div>
                                    <div className='attach-row'>
                                        {/* <div className='d-flex flex-row gap-2 align-items-center cursor-pointer' onClick={() => fileInputRef.current?.click()} >
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept=".jpg,.png,.pdf,.doc,.docx,.txt,.rtf"
                                                style={{ display: "none" }}
                                                onChange={handleFileChange}
                                            />
                                            <label className='file-label'>Attach File</label>
                                            <img className='file-icon' alt="" src={require('../assets/images/attachment.svg').default} />
                                        </div> */}
                                        {feedback?.loading ?
                                            <div className='btn-send'>
                                                <div class="spinner" />
                                                <span>Sending</span>
                                            </div>
                                            :
                                            <div className='btn-send' onClick={handleSubmit}>
                                                <span>Send</span>
                                            </div>
                                        }
                                    </div>
                                </div>
                            }
                        </div>

                        {/* sales */}
                        {canSeePlanningBoard && (
                            <Spin spinning={!statusState.loading && salesChart.loading}>
                                <div className='sales'>
                                    <div className='sales-header'>
                                        <label className='label-sales'>Sales</label>
                                        <div className={`right-content ${isComparison ? '' : 'no-multi'}`}>
                                            {isComparison ?
                                                <div className='multi-segmented-control'>
                                                    <Segmented
                                                        options={segmentedOptions.week}
                                                        selected={selecetdSales}
                                                        onChange={(value) => handleSalesTabChange(value)}
                                                        comparison={segmentedOptions.week.some(i => i.slug === selecetdSales)}
                                                    />
                                                    <Segmented
                                                        options={segmentedOptions.month}
                                                        selected={selecetdSales}
                                                        onChange={(value) => handleSalesTabChange(value)}
                                                        comparison={segmentedOptions.month.some(i => i.slug === selecetdSales)}
                                                    />
                                                    <Segmented
                                                        options={segmentedOptions.year}
                                                        selected={selecetdSales}
                                                        onChange={(value) => handleSalesTabChange(value)}
                                                        comparison={segmentedOptions.year.some(i => i.slug === selecetdSales)}
                                                    />
                                                </div>
                                                : <Segmented
                                                    options={[{ label: 'This Week', slug: 'this_week' }, { label: 'Last Week', slug: 'last_week' }, { label: 'This Month', slug: 'this_month' }, { label: 'Last Month', slug: 'last_month' }, { label: 'This Year', slug: moment().year() }, { label: 'Last Year', slug: moment().subtract(1, "year").year() }]}
                                                    selected={selecetdSales}
                                                    onChange={(value) => handleSalesTabChange(value)}
                                                />
                                            }
                                            <div className='gap-2 d-flex flex-row align-items-center'>
                                                <span className='comparison-label'>Comparison</span>
                                                <Switch
                                                    defaultChecked
                                                    onChange={(e) => {
                                                        setComparison(e)
                                                        getSalesChartData(selecetdSales, true, e)
                                                        localStorage.setItem('salesChartComparison', e)
                                                    }}
                                                    checked={isComparison}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    {chartData?.length > 0 &&
                                        <div className='chart-container'>
                                            <Chart
                                                chartType="LineChart"
                                                width="100%"
                                                height="400px"
                                                data={chartData}
                                                options={options}
                                                legendToggle
                                            />
                                        </div>
                                    }
                                </div>
                            </Spin>
                        )}
                    </div>
                
            </Spin>
        </div>
    )
}


const mapStateToProps = (state) => {
    return {
        statusState: state.DashboardReducer.statusState,
        completedStates: state.DashboardReducer.completedStates,
        scheduledStates: state.DashboardReducer.scheduledStates,
        feedback: state.DashboardReducer.feedback,
        salesChart: state.DashboardReducer.salesChart,
        feedbackCategory: state.DashboardReducer.feedbackCategory
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        getDashboardStatusStates: (loading) => dispatch(getDashboardStatusStates(loading)),
        getCompletedStates: (filter, loading) => dispatch(getCompletedStates(filter, loading)),
        getScheduledStatesData: (filter, loading) => dispatch(getScheduledStatesData(filter, loading)),
        getSalesChartData: (year, loading, isComparison) => dispatch(getSalesChartData({ year, isComparison }, loading)),
        getFeedbackCategory: () => dispatch(getFeedbackCategory()),
        onSubmitFeedback: (payload) => dispatch(submitFeedback(payload))
    }
}
export default connect(mapStateToProps, mapDispatchToProps)(Dashboard)
